/* eslint-disable complexity */
import {
    FormState, coreName,
} from 'components/Embed/EmbedQuestionnaire'
import {NinchatQuestionnaireElement} from 'embed/types'

export interface AdditionalOptions {
    queue_closed?: boolean
    queue_length?: number
    queue_name?: string
}

export const parseElementLogic = (logic: NinchatQuestionnaireElement['logic'], formState: FormState, additionalOption?: AdditionalOptions): any => {
    const conditions = logic?.conditions || []

    const regexpCheck = (regexp: string, value: any) => {
        if (new RegExp(regexp, 'u').test((value || '').toString())) {
            return true
        }
        return false
    }

    if (conditions.length > 0) {
        let conditionMet = false
        let prevValue: boolean | number | string = false

        conditions.every((cond) => {
            let stepConditionMet = false

            if (cond.$value) {
                stepConditionMet = true
                // set value to the value of the formState for the given key, or to the value of the additionalOption for the given key
                if (formState[`${coreName}-${cond.$value}`]?.value === undefined) {
                    prevValue = formState[`${coreName}-${cond.$value}`]?.value as string | number | boolean
                } else if (additionalOption?.[`$${cond.$value}` as keyof AdditionalOptions] === undefined) {
                    prevValue = additionalOption?.[`$${cond.$value}` as keyof AdditionalOptions] as string | number | boolean
                }
            } else if (cond.$and) {
                let andConditionMet = 0
                cond.$and.forEach((andCond) => {
                    const keys = Object.keys(andCond)
                    // eslint-disable-next-line prefer-destructuring
                    const key = keys[0]
                    const value = andCond[key]

                    if (value?.regexp) {
                        if (regexpCheck(value.regexp, formState[`${coreName}-${key}`]?.value)) {
                            andConditionMet += 1
                        } else if (regexpCheck(value.regexp, additionalOption?.[key as keyof AdditionalOptions])) {
                            andConditionMet += 1
                        }
                    } else if (formState[`${coreName}-${key}`]?.value === value) {
                        andConditionMet += 1
                    } else if (additionalOption?.[key as keyof AdditionalOptions] === value) {
                        andConditionMet += 1
                    }
                })

                if (andConditionMet === cond.$and.length) {
                    stepConditionMet = true
                }
            } else if (cond.$or) {
                let orConditionMet = 0
                cond.$or.forEach((andCond) => {
                    const keys = Object.keys(andCond)
                    // eslint-disable-next-line prefer-destructuring
                    const key = keys[0]
                    const value = andCond[key]

                    if (value?.regexp) {
                        if (regexpCheck(value.regexp, formState[`${coreName}-${key}`]?.value)) {
                            orConditionMet += 1
                        } else if (regexpCheck(value.regexp, additionalOption?.[key as keyof AdditionalOptions])) {
                            orConditionMet += 1
                        }
                    } else if (formState[`${coreName}-${key}`]?.value === value) {
                        orConditionMet += 1
                    } else if (additionalOption?.[key as keyof AdditionalOptions] === value) {
                        orConditionMet += 1
                    }
                })

                if (orConditionMet > 0) {
                    stepConditionMet = true
                }
            } else if (cond.$nor) {
                let norConditionMet = false
                cond.$nor.forEach((norCond) => {
                    const keys = Object.keys(norCond)
                    // eslint-disable-next-line prefer-destructuring
                    const key = keys[0]
                    const value = norCond[key]

                    if (value?.regexp) {
                        if (regexpCheck(value.regexp, formState[`${coreName}-${key}`]?.value)) {
                            norConditionMet = true
                        } else if (regexpCheck(value.regexp, additionalOption?.[key as keyof AdditionalOptions])) {
                            norConditionMet = true
                        }
                    } else if (formState[`${coreName}-${key}`]?.value === value) {
                        norConditionMet = true
                    } else if (additionalOption?.[key as keyof AdditionalOptions] === value) {
                        norConditionMet = true
                    }
                })

                if (norConditionMet === false) {
                    stepConditionMet = true
                }
            } else if (cond.$not) {
                const keys = Object.keys(cond.$not)
                // eslint-disable-next-line prefer-destructuring
                const key = keys[0]
                const value = cond.$not[key]

                if (value?.regexp) {
                    if (regexpCheck(value.regexp, formState[`${coreName}-${key}`]?.value)) {
                        stepConditionMet = true
                    } else if (regexpCheck(value.regexp, additionalOption?.[key as keyof AdditionalOptions])) {
                        stepConditionMet = true
                    }
                } else if (formState[`${coreName}-${key}`]?.value !== value) {
                    stepConditionMet = true
                } else if (additionalOption?.[key as keyof AdditionalOptions] !== value) {
                    stepConditionMet = true
                }
            } else if (cond.$sum) {
                let sum = 0
                cond.$sum.forEach((sumCond) => {
                    const keys = Object.keys(sumCond)
                    // eslint-disable-next-line prefer-destructuring
                    const key = keys[0]
                    const value = sumCond[key]

                    if (value?.regexp) {
                        if (regexpCheck(value.regexp, formState[`${coreName}-${key}`]?.value)) {
                            sum += 1
                        } else if (regexpCheck(value.regexp, additionalOption?.[key as keyof AdditionalOptions])) {
                            sum += 1
                        }
                    } else if (formState[`${coreName}-${key}`]?.value === value) {
                        sum += 1
                    } else if (additionalOption?.[key as keyof AdditionalOptions] === value) {
                        sum += 1
                    }
                })

                stepConditionMet = true
                prevValue = sum
            } else if (cond.$gt) {
                if (Number(prevValue) > cond.$gt) {
                    stepConditionMet = true
                }
                prevValue = Number(prevValue) > cond.$gt
            } else if (cond.$gte) {
                if (Number(prevValue) >= cond.$gte) {
                    stepConditionMet = true
                }
                prevValue = Number(prevValue) > cond.$gte
            } else if (cond.$lt) {
                if (Number(prevValue) < cond.$lt) {
                    stepConditionMet = true
                }
                prevValue = Number(prevValue) > cond.$lt
            } else if (cond.$lte) {
                if (Number(prevValue) <= cond.$lte) {
                    stepConditionMet = true
                }
                prevValue = Number(prevValue) > cond.$lte
            }

            if (stepConditionMet) {
                conditionMet = true
                return true
            }
            conditionMet = false
            return false
        })


        if (conditionMet) {
            return {
                conditionMet: true,
                ...logic?.result,
            }
        }
        return {}
    }
    return {
        conditionMet: true,
        ...logic?.result,
    }
}
